import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, Button, TextField, Grid } from "@mui/material";

const SplitOrderModal = ({
  show,
  handleClose,
  splitOption,
  rowIndex,
  order,
  setOrder,
  handleInputChange,
  recalculateMultipleRows,
  onSplitComplete,
}) => {
  const [rows, setRows] = useState([
    { width: "", height: "", deduction: "", addition: "" },
    { width: "", height: "", deduction: "", addition: "" },
    { width: "", height: "", deduction: "", addition: "" },
  ]);
  const [error, setError] = useState("");

  const fractions = [
    { fraction: "1/8", value: 0.125 },
    { fraction: "1/4", value: 0.25 },
    { fraction: "3/8", value: 0.375 },
    { fraction: "1/2", value: 0.5 },
    { fraction: "5/8", value: 0.625 },
    { fraction: "3/4", value: 0.75 },
    { fraction: "7/8", value: 0.875 },
  ];

  const toFractionalString = (value, roundDown = false) => {
    if (!value || isNaN(value)) return "";
    const whole = Math.floor(value);
    const decimal = value - whole;
    if (decimal < 0.01) return whole.toString();

    let closestFraction;
    if (roundDown) {
      closestFraction = fractions
        .filter((f) => f.value <= decimal + 0.01)
        .reduce((prev, curr) =>
          Math.abs(curr.value - decimal) < Math.abs(prev.value - decimal)
            ? curr
            : prev,
          { fraction: "0", value: 0 }
        );
    } else {
      closestFraction = fractions.reduce((prev, curr) =>
        Math.abs(curr.value - decimal) < Math.abs(prev.value - decimal)
          ? curr
          : prev,
        fractions[0]
      );
    }

    if (closestFraction.value < 0.01) return whole.toString();
    return `${whole} ${closestFraction.fraction}`;
  };

  const parseInput = (input) => {
    if (!input || input.trim() === "") return 0;

    const trimmedInput = input.trim();
    const parts = trimmedInput.split(" ");

    // Handle single whole number
    if (parts.length === 1) {
      const value = parseFloat(parts[0]);
      return isNaN(value) ? 0 : value; // Treat invalid inputs as 0 for auto-adjustment
    }

    // Handle whole number with fraction (e.g., "5 1/4")
    if (parts.length === 2) {
      const whole = parseInt(parts[0]) || 0;
      const fractionPart = parts[1].trim();

      // If fraction part is empty (e.g., "5 "), treat as whole number
      if (fractionPart === "") {
        return whole;
      }

      // Check if fraction part is a valid fraction
      const fraction = fractions.find((f) => f.fraction === fractionPart);
      if (!fraction) return whole; // Treat invalid fraction as whole number for auto-adjustment

      return whole + fraction.value;
    }

    // Any other format is treated as 0 for auto-adjustment
    return 0;
  };

  const validateFractionalInput = (input) => {
    const trimmedInput = input.trim();
    if (trimmedInput === "") return ""; 

    const parts = trimmedInput.split(" ");
    if (parts.length === 2 && parts[1].trim() === "") {
      return ""; 
    }

    const parsedValue = parseInput(trimmedInput);
    if (isNaN(parsedValue)) {
      return "Invalid fraction. Use whole numbers or fractions like 1 1/4 (from 1/8 to 7/8).";
    }
    return "";
  };

  const originalRow = order[rowIndex] || {};
  const originalWidth = parseInput(originalRow.width) || 0;
  const originalHeight = parseInput(originalRow.height) || 0;

  useEffect(() => {
    if (show) {
      if (splitOption === 2) {
        const halfWidth = originalWidth / 2;
        const roundedWidth = toFractionalString(halfWidth, true);
        const roundedValue = parseInput(roundedWidth);
        const remainingWidth = originalWidth - roundedValue;
        const secondWidth = toFractionalString(remainingWidth);
        setRows([
          { width: roundedWidth, height: originalRow.height || "", deduction: originalRow.deduction || "", addition: originalRow.addition || "" },
          { width: secondWidth, height: originalRow.height || "", deduction: originalRow.deduction || "", addition: originalRow.addition || "" },
          { width: "", height: "", deduction: "", addition: "" },
        ]);
      } else if (splitOption === 3) {
        const thirdWidth = originalWidth / 3;
        const roundedThird = toFractionalString(thirdWidth, true);
        const roundedValue = parseInput(roundedThird);
        const remainingAfterTwo = originalWidth - roundedValue * 2;
        const thirdPart = toFractionalString(remainingAfterTwo);
        setRows([
          { width: roundedThird, height: originalRow.height || "", deduction: originalRow.deduction || "", addition: originalRow.addition || "" },
          { width: roundedThird, height: originalRow.height || "", deduction: originalRow.deduction || "", addition: originalRow.addition || "" },
          { width: thirdPart, height: originalRow.height || "", deduction: originalRow.deduction || "", addition: originalRow.addition || "" },
        ]);
      }
      setError("");
    }
  }, [show, splitOption, originalWidth, originalHeight, originalRow]);

  const handleWidthChange = (index, value) => {
    // Update state immediately to ensure responsiveness
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], width: value };
    
    // Skip auto-adjustment for the last row (it's auto-adjusted)
    if (index !== splitOption - 1) {
      // Calculate remaining width for the last row
      const otherWidths = newRows
        .slice(0, splitOption - 1)
        .reduce((sum, row) => sum + parseInput(row.width), 0);
      const lastRowIndex = splitOption - 1;
      const remainingWidth = originalWidth - otherWidths;

      // Update the last row's width, even for partial inputs
      newRows[lastRowIndex] = {
        ...newRows[lastRowIndex],
        width: remainingWidth >= 0 ? toFractionalString(remainingWidth) : "",
      };
    }

    setRows(newRows);
    setError(""); 
  };

  const handleHeightChange = (index, value) => {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], height: value };
    setRows(newRows);
    setError(""); 
  };

  const handleInputBlur = (index, field, value) => {
    
    const errorMsg = validateFractionalInput(value);
    if (errorMsg) {
      setError(errorMsg);
      return;
    }

    if (field === "width" && index !== splitOption - 1) {
      const parsedValue = parseInput(value);
      if (parsedValue < 0 || parsedValue > originalWidth) {
        setError(`Width must be between 0 and ${originalWidth}`);
        return;
      }

      // Recalculate remaining 
      const newRows = [...rows];
      const otherWidths = newRows
        .slice(0, splitOption - 1)
        .reduce((sum, row) => sum + parseInput(row.width), 0);
      const lastRowIndex = splitOption - 1;
      const remainingWidth = originalWidth - otherWidths;

      if (remainingWidth < 0) {
        setError(`Widths exceed total width of ${originalWidth}`);
        return;
      }

      newRows[lastRowIndex] = {
        ...newRows[lastRowIndex],
        width: toFractionalString(remainingWidth),
      };
      setRows(newRows);
    }

    setError("");
  };

  const handleSave = () => {
    const widths = rows.slice(0, splitOption).map((row) => parseInput(row.width));
    const heights = rows.slice(0, splitOption).map((row) => parseInput(row.height));

    if (widths.some((width) => isNaN(width))) {
      setError("Invalid width format. Use whole numbers or fractions like 1 1/4.");
      return;
    }
    if (heights.some((height) => isNaN(height))) {
      setError("Invalid height format. Use whole numbers or fractions like 1 1/4.");
      return;
    }

    const totalWidth = widths.reduce((sum, width) => sum + width, 0);
    if (Math.abs(totalWidth - originalWidth) > 0.125) {
      setError(`Widths must sum to ${toFractionalString(originalWidth)}`);
      return;
    }

    if (widths.some((width) => width <= 0)) {
      setError("Widths must be greater than 0");
      return;
    }

    if (heights.some((height) => height <= 0)) {
      setError("Heights must be greater than 0");
      return;
    }

    const labels = splitOption === 2 ? ["Left", "Right"] : ["Left", "Middle", "Right"];
    const newRows = rows.slice(0, splitOption).map((row, idx) => ({
      width: toFractionalString(widths[idx]),
      height: toFractionalString(heights[idx]),
      remark: `Lineup:${labels[idx]}`,
      deduction: originalRow.deduction || "",
      addition: originalRow.addition || "",
      control: originalRow.control || "",
      lift: originalRow.lift || "",
      liftStyle: originalRow.liftStyle || "",
      mount: originalRow.mount || "",
      location: originalRow.location || "",
      tile: originalRow.tile || "",
    }));

    setOrder((prevOrder) => {
      if (rowIndex < 0 || rowIndex >= prevOrder.length) {
        console.error(`Invalid rowIndex: ${rowIndex}, prevOrder length: ${prevOrder.length}`);
        return prevOrder;
      }

      const updatedOrder = [...prevOrder];
      updatedOrder.splice(rowIndex, 1, ...newRows);
      const indices = Array.from({ length: splitOption }, (_, idx) => rowIndex + idx);
      const recalculatedOrder = recalculateMultipleRows(indices, updatedOrder);

      return recalculatedOrder;
    });

    onSplitComplete(rowIndex, splitOption);
    handleClose();
  };

  if (splitOption !== 2 && splitOption !== 3) {
    return null;
  }

  const labels = splitOption === 2 ? ["Left", "Right"] : ["Left", "Middle", "Right"];

  return (
    <Modal
      open={show}
      onClose={handleClose}
      aria-labelledby="split-order-modal-title"
      aria-describedby="split-order-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "white",
          color: "black",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Typography
          id="split-order-modal-title"
          variant="h6"
          component="h2"
          sx={{ color: "black" }}
        >
          Split Order
        </Typography>
        <Typography sx={{ mt: 2, color: "black" }}>
          Splitting into {splitOption} parts. Total width must equal {toFractionalString(originalWidth)}.
        </Typography>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          {rows.slice(0, splitOption).map((row, index) => (
            <React.Fragment key={index}>
              <Grid item xs={12}>
                <Typography sx={{ color: "black", fontWeight: "bold" }}>
                  {labels[index]}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Width"
                  type="text"
                  value={row.width}
                  onChange={(e) => handleWidthChange(index, e.target.value)}
                  onBlur={() => handleInputBlur(index, "width", row.width)}
                  fullWidth
                  sx={{ "& .MuiInputBase-input": { color: "black" } }}
                  error={!!error}
                  disabled={index === splitOption - 1}
                  helperText={index === splitOption - 1 ? "Auto-adjusted" : "Enter as '5' or '5 1/4'"}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Height"
                  type="text"
                  value={row.height}
                  onChange={(e) => handleHeightChange(index, e.target.value)}
                  onBlur={() => handleInputBlur(index, "height", row.height)}
                  fullWidth
                  sx={{ "& .MuiInputBase-input": { color: "black" } }}
                  error={!!error}
                  helperText="Enter as '5' or '5 1/4'"
                />
              </Grid>
            </React.Fragment>
          ))}
        </Grid>

        {error && (
          <Typography sx={{ color: "red", mt: 2 }}>{error}</Typography>
        )}

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="outlined"
            sx={{ color: "black", borderColor: "black" }}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#1976d2", color: "white" }}
            onClick={handleSave}
            disabled={!!error}
          >
            Save Split
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SplitOrderModal;